/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import type { AgencyDashboardItem } from '@pages/authenticated/manage-agencies/ManageAgenciesCards';

/**
 * Dev-cockpit scenario data used by useAgenciesData when REACT_APP_DEBUG=true.
 * Kept as a production module (not inside __mocks__) so it ships in the npm
 * package and is available to the release pipeline's Vite build.
 */

export const emptyAgenciesMock: AgencyDashboardItem[] = [];

export const fiveAgenciesMock: AgencyDashboardItem[] = [
  {
    id: 'agency-001',
    name: 'Imobiliária Centro',
    cnpj: '12.345.678/0001-00',
    postal_code: '01001-000',
    city: 'São Paulo',
    number: '100',
    neighborhood: 'Centro',
    creation_date: '2025-03-15T10:00:00Z',
    state: 'SP',
    street: 'Rua Direita',
    description: 'Principal agência do centro de SP',
    image: undefined,
    status: 'active',
    active_subscription_id: 'sub_001_centro',
    subscription_status: 'active',
    payment_status: 'approved',
    plan_id: 'ruby-agency-monthly-housien',
    members: [
      { id: 'member-1', email: 'admin@centro.com',  first_name: 'Ana',    last_name: 'Silva', role: 'admin' },
      { id: 'member-2', email: 'agent@centro.com',  first_name: 'Carlos', last_name: 'Lima',  role: 'agent' },
    ],
  },
  {
    id: 'agency-002',
    name: 'Cormier & Associados',
    cnpj: '98.765.432/0001-11',
    postal_code: '88010-000',
    city: 'Florianópolis',
    number: '250',
    neighborhood: 'Centro',
    creation_date: '2025-06-01T14:30:00Z',
    state: 'SC',
    street: 'Av. Beira Mar Norte',
    description: 'Especialistas em imóveis litorâneos',
    image: undefined,
    status: 'payment_failed',
    active_subscription_id: 'sub_002_cormier',
    subscription_status: 'active',
    payment_status: 'failed',
    plan_id: 'emerald-solo-monthly-housien',
    members: [
      { id: 'member-c1', email: 'admin@cormier.com', first_name: 'Marina', last_name: 'Cormier', role: 'admin' },
    ],
  },
  {
    id: 'agency-003',
    name: 'Mountain View Realty',
    cnpj: '11.222.333/0001-44',
    postal_code: '80060-000',
    city: 'Curitiba',
    number: '42',
    neighborhood: 'Batel',
    creation_date: '2025-09-20T08:00:00Z',
    state: 'PR',
    street: 'Rua Comendador Araújo',
    complement: 'Sala 501',
    image: undefined,
    status: 'active',
    active_subscription_id: 'sub_003_mountain',
    subscription_status: 'active',
    payment_status: 'approved',
    plan_id: 'diamond-agency-monthly-housien',
    members: [
      { id: 'member-3', email: 'owner@mountain.com', first_name: 'Roberto', last_name: 'Mendes', role: 'admin' },
    ],
  },
  {
    id: 'agency-004',
    name: 'Nordeste Imóveis Premium',
    cnpj: '55.666.777/0001-88',
    postal_code: '50030-000',
    city: 'Recife',
    number: '1500',
    neighborhood: 'Boa Viagem',
    creation_date: '2026-01-10T11:15:00Z',
    state: 'PE',
    street: 'Av. Conselheiro Aguiar',
    image: undefined,
    status: 'active',
    active_subscription_id: 'sub_004_nordeste',
    subscription_status: 'active',
    payment_status: 'approved',
    plan_id: 'ruby-agency-monthly-housien',
    members: [
      { id: 'member-4', email: 'admin@nordeste.com', first_name: 'Felipe', last_name: 'Araújo', role: 'admin' },
    ],
  },
  {
    id: 'agency-005',
    name: 'Sul Empreendimentos',
    cnpj: '22.333.444/0001-55',
    postal_code: '90040-060',
    city: 'Porto Alegre',
    number: '800',
    neighborhood: 'Moinhos de Vento',
    creation_date: '2026-02-20T09:00:00Z',
    state: 'RS',
    street: 'Rua Padre Chagas',
    image: undefined,
    status: 'active',
    active_subscription_id: 'sub_005_sul',
    subscription_status: 'active',
    payment_status: 'approved',
    plan_id: 'emerald-solo-monthly-housien',
    members: [
      { id: 'member-5', email: 'admin@sul.com', first_name: 'Helena', last_name: 'Costa', role: 'admin' },
    ],
  },
];

export const paymentPendingAgencyMock: AgencyDashboardItem[] = [fiveAgenciesMock[1]];

export const oneActiveMock: AgencyDashboardItem[] = [fiveAgenciesMock[0]];

export const onePendingMock: AgencyDashboardItem[] = [{
  ...fiveAgenciesMock[0],
  id: 'agency-pending-solo',
  name: 'Nova Imobiliária',
  city: 'Brasília',
  state: 'DF',
  status: 'no_subscription',
  active_subscription_id: null,
  subscription_status: null,
  payment_status: null,
  plan_id: null,
}];

export const twoActiveMock: AgencyDashboardItem[] = [
  fiveAgenciesMock[0],
  fiveAgenciesMock[4],
];

export const twoMixedMock: AgencyDashboardItem[] = [
  fiveAgenciesMock[0],
  fiveAgenciesMock[1],
];

export const twoPendingMock: AgencyDashboardItem[] = [
  {
    ...fiveAgenciesMock[0],
    id: 'agency-pend-a',
    status: 'no_subscription',
    active_subscription_id: null,
    subscription_status: null,
    payment_status: null,
    plan_id: null,
  },
  {
    ...fiveAgenciesMock[4],
    id: 'agency-pend-b',
    status: 'no_subscription',
    active_subscription_id: null,
    subscription_status: null,
    payment_status: null,
    plan_id: null,
  },
];

export const fiveMixedMock: AgencyDashboardItem[] = [
  fiveAgenciesMock[0],
  { ...fiveAgenciesMock[1], status: 'payment_failed', subscription_status: 'active', payment_status: 'failed' },
  { ...fiveAgenciesMock[2], status: 'suspended', subscription_status: 'suspended', payment_status: 'failed' },
  { ...fiveAgenciesMock[3], status: 'cancelled', subscription_status: 'cancelled', payment_status: 'refunded' },
  { ...fiveAgenciesMock[4], status: 'no_subscription', active_subscription_id: null, subscription_status: null, payment_status: null, plan_id: null },
];
